/**
 * Gmail Scout - Browser-based email scanning for commission files
 *
 * This is the "Client-Side Scout" from the White-Glove Onboarding plan.
 * Key principles (Jeff Dean approach):
 * - Push compute to the edge (user's browser does the work)
 * - Short-lived tokens only (no refresh tokens stored)
 * - User sees exactly what's being scanned (transparency)
 * - Zero compute cost for Valutten
 *
 * Flow:
 * 1. User clicks "Connect Gmail" → OAuth redirect
 * 2. Receive access token (1 hour, no refresh)
 * 3. Browser searches Gmail for commission emails
 * 4. Download attachments directly in browser
 * 5. Upload to Valutten via signed URLs
 */

// Gmail API types
import { extractEntityCode } from '@valutten/shared-utils';

export interface GmailMessage {
  id: string;
  threadId: string;
  internalDate: string;
  payload: GmailMessagePart;
}

export interface GmailMessagePart {
  partId?: string;
  mimeType: string;
  filename?: string;
  headers: Array<{ name: string; value: string }>;
  body: {
    attachmentId?: string;
    size: number;
    data?: string;
  };
  parts?: GmailMessagePart[];
}

export interface ScoutedFile {
  filename: string;
  mimeType: string;
  size: number;
  attachmentId: string;
  messageId: string;
  emailDate: Date;
  emailSubject: string;
  senderEmail: string;
  // From aggregator matching
  aggregatorCode: string | null;
  /** Entity code extracted from filename prefix (e.g. W4FI104, ENTO100).
   *  Falls back to aggregatorCode when matchAggregator() succeeds,
   *  or uses generic prefix extraction for unknown entity codes. */
  entityCode: string | null;
  runNumber: number | null;
  periodMonth: string | null;
  /** Human-friendly entity name extracted from the filename (e.g. "Entossey" from "V316295PL Entossey Pty Ltd ...").
   *  Falls back to undefined when no name can be extracted. */
  entityName?: string;
  /** Whether this is a data file (CSV/XLSX) or an RCTI PDF */
  fileType: 'data' | 'rcti';
  // For deduplication (computed when downloaded)
  fileHash?: string;
  content?: ArrayBuffer;
}

export interface ScanProgress {
  phase: 'searching' | 'listing' | 'analyzing' | 'complete' | 'error';
  messagesFound: number;
  messagesScanned: number;
  filesFound: number;
  filesMatched: number;
  currentMessage?: string;
  error?: string;
}

export type ProgressCallback = (progress: ScanProgress) => void;

// Known commission senders (from aggregatorRegistry)
export const KNOWN_COMMISSION_SENDERS = [
  'commissions@loanmarket.com.au',
  'plan.commissions@loanmarketgroup.com',
  'statements@afgfinance.com.au',
  'commissions@afgonline.com.au',
  'commissions@wlth.com',
  'noreply@orde.com.au',
];

// Known commission filename patterns (catches files from unknown senders or forwarded emails)
const KNOWN_COMMISSION_FILENAMES = [
  'ENTO',
  'ENBB',
  'ENTM',
  'ENTORS',
  'ENTORUM',
  'WLTH',
  'ORDE',
  'RCTI-',
  'COMM_STMT',
];

// Valid file extensions
const VALID_EXTENSIONS = ['xlsx', 'xls', 'csv', 'pdf'];

/**
 * Initialize Google API client for Gmail access.
 * Must be called before any Gmail operations.
 */
export async function initGmailClient(accessToken: string): Promise<void> {
  // Load gapi if not already loaded
  if (!window.gapi) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google API'));
      document.body.appendChild(script);
    });
  }

  // Initialize gapi client
  await new Promise<void>((resolve) => {
    window.gapi.load('client', resolve);
  });

  // Set access token
  window.gapi.client.setToken({ access_token: accessToken });

  // Initialize Gmail API
  await window.gapi.client.init({
    discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest'],
  });
}

/**
 * Build search query for commission emails.
 *
 * In normal mode: searches by known senders + known filename patterns.
 * In staging mode: searches ONLY by sender patterns (no filename filtering),
 * because new subscribers have unknown entity codes/filenames.
 */
function buildSearchQuery(
  senderPatterns: string[] = KNOWN_COMMISSION_SENDERS,
  afterDate?: Date,
  stagingMode = false,
  hasCustomSenders = false,
  beforeDate?: Date
): string {
  const parts: string[] = [];

  const orClauses: string[] = [];

  if (senderPatterns.length > 0) {
    orClauses.push(...senderPatterns.map((sender) => `from:${sender}`));
  }

  if (!stagingMode && !hasCustomSenders) {
    // Normal mode: also match by known filename patterns
    // (catches files from unknown senders or forwarded emails)
    // Skipped when custom senders are provided — the user is telling us where to look,
    // so we trust all valid attachments from those senders.
    orClauses.push(...KNOWN_COMMISSION_FILENAMES.map((pattern) => `filename:${pattern}`));
  }

  if (orClauses.length > 0) {
    parts.push(`(${orClauses.join(' OR ')})`);
  }

  // Must have attachment
  parts.push('has:attachment');

  // Date filter (for incremental sync or date range scanning)
  if (afterDate) {
    const dateStr = afterDate.toISOString().split('T')[0].replace(/-/g, '/');
    parts.push(`after:${dateStr}`);
  }
  if (beforeDate) {
    const dateStr = beforeDate.toISOString().split('T')[0].replace(/-/g, '/');
    parts.push(`before:${dateStr}`);
  }

  return parts.join(' ');
}

/**
 * Search Gmail for commission emails.
 */
async function searchMessages(
  query: string,
  maxResults: number = 500
): Promise<string[]> {
  const messageIds: string[] = [];
  let pageToken: string | undefined;

  do {
    const response = await window.gapi.client.gmail.users.messages.list({
      userId: 'me',
      q: query,
      maxResults: Math.min(100, maxResults - messageIds.length),
      pageToken,
    });

    const messages = response.result.messages || [];
    messageIds.push(...messages.map((m: { id: string }) => m.id));

    pageToken = response.result.nextPageToken;
  } while (pageToken && messageIds.length < maxResults);

  return messageIds;
}

/**
 * Get full message details including attachments.
 */
async function getMessageDetails(messageId: string): Promise<GmailMessage> {
  const response = await window.gapi.client.gmail.users.messages.get({
    userId: 'me',
    id: messageId,
    format: 'full',
  });

  return response.result as GmailMessage;
}

/**
 * Extract attachment info from message payload.
 */
function extractAttachments(
  payload: GmailMessagePart,
  attachments: Array<{
    filename: string;
    mimeType: string;
    attachmentId: string;
    size: number;
  }> = []
): typeof attachments {
  // Check if this part is an attachment
  if (payload.filename && payload.body?.attachmentId) {
    attachments.push({
      filename: payload.filename,
      mimeType: payload.mimeType,
      attachmentId: payload.body.attachmentId,
      size: payload.body.size,
    });
  }

  // Recursively check nested parts
  if (payload.parts) {
    for (const part of payload.parts) {
      extractAttachments(part, attachments);
    }
  }

  return attachments;
}

/**
 * Get header value from message headers.
 */
function getHeader(
  headers: Array<{ name: string; value: string }>,
  name: string
): string | undefined {
  const header = headers.find(
    (h) => h.name.toLowerCase() === name.toLowerCase()
  );
  return header?.value;
}

/**
 * Check if filename has valid extension for commission files.
 */
function hasValidExtension(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? VALID_EXTENSIONS.includes(ext) : false;
}

/**
 * Check if file is an individual broker PDF (to exclude).
 * Pattern: "ENTO100 Damien Roylance AGMT.pdf" <- EXCLUDE
 */
function isIndividualBrokerFile(filename: string): boolean {
  const pattern = /^(EN[A-Z]{1,2}\d+|V\d+PL)\s+[A-Z][a-z]+\s+[A-Z][a-z]+\s+(AGMT|MEM|BA|Summary)/i;
  return pattern.test(filename);
}

/**
 * Check if file is a recognized RCTI PDF (vs data file).
 * Browser-side equivalent of parseRCTIFilename() + detectRCTIType().
 */
function isRctiPdf(filename: string): boolean {
  if (!/\.pdf$/i.test(filename)) return false;
  // Loan Market: RCTI-{Company}-{YYYYMMDD}-{PaymentNumber}.pdf (tolerates " (1)" download suffix)
  if (/RCTI[-_\s].+[-_\s]\d{8}[-_\s]\d+/i.test(filename)) return true;
  // ORDE legacy: BrokerTransaction RCTI ORD-{number}.pdf
  if (/BrokerTransaction\s+RCTI\s+ORD-\d+/i.test(filename)) return true;
  // ORDE direct: {Company} RCTI-YYYYMMDD.pdf
  if (/\s+RCTI-\d{8}\.pdf$/i.test(filename)) return true;
  // Loan Market entity + V-code entity: ENTO101 Summary RUN 1 Feb 26.pdf, V316295PL ... AGMT RUN 3 Mar 26.pdf
  if (/^[A-Z]+\d+[A-Z]*\s.+RUN\s*\d+\s.+\.pdf$/i.test(filename)) return true;
  return false;
}

/**
 * Extract entity code prefix from filename (e.g. "W4FI104 LW RUN 3.xlsx" → "W4FI104").
 * Browser-safe equivalent of extractEntityCode() from @valutten/shared-utils.
 * Used as fallback when matchAggregator() doesn't recognize the pattern.
 */
function extractEntityCodeFromFilename(filename: string): string | null {
  // Delegates to the one shared implementation. This was an inline copy that
  // never received the 2026-08-28 dot fix, so `MAVI1.00 AGMT ...` resolved to
  // null here while the staged path resolved it to MAVI100. The helper is pure
  // string work with no node dependencies, so it is browser-safe.
  return extractEntityCode(filename);
}

/**
 * Extract a human-friendly entity name from the filename.
 * Works for V-code files, COMM_STMT files, and RCTI files where
 * the company name is embedded in the filename.
 *
 * Examples:
 *   "V316295PL Entossey Pty Ltd (Trl Pur) AGMT RUN 3 Mar 26.pdf" → "Entossey"
 *   "COMM_STMT_ENTOURAGE CRANBOURNE PTY LTD-MASTER-1113194.csv"  → "Cranbourne"
 *   "RCTI-ENTOURAGE CRANBOURNE PTY LTD-20260306-1113194.pdf"     → "Cranbourne"
 */
function extractEntityNameFromFilename(filename: string): string | null {
  // V-code files: V\d+PL <CompanyName> (qualifier) AGMT/LW/MEM/Summary ...
  const vcodeMatch = filename.match(/^V\d+PL\s+(.+?)(?:\s*\(.*?\))?\s+(?:AGMT|LW|MEM|Summary)\b/i);
  if (vcodeMatch) {
    return cleanCompanyName(vcodeMatch[1]);
  }

  // COMM_STMT files: COMM_STMT_ENTOURAGE CRANBOURNE PTY LTD-MASTER-...
  // or COMM_STMT-CRANBOURNE PTY LTD-MASTER-...
  const commStmtMatch = filename.match(/COMM_STMT[-_](?:ENTOURAGE\s+)?(.+?)[-_]MASTER/i);
  if (commStmtMatch) {
    return cleanCompanyName(commStmtMatch[1]);
  }

  // RCTI files: RCTI-ENTOURAGE CRANBOURNE PTY LTD-20260306-...
  const rctiMatch = filename.match(/RCTI[-_](?:ENTOURAGE\s+)?(.+?)[-_]\d{8}/i);
  if (rctiMatch) {
    return cleanCompanyName(rctiMatch[1]);
  }

  return null;
}

/**
 * Strip corporate suffixes (Pty Ltd, etc.) and title-case the result.
 * "CRANBOURNE PTY LTD" → "Cranbourne"
 * "Entossey Pty Ltd"   → "Entossey"
 */
function cleanCompanyName(raw: string): string {
  // Remove corporate suffixes
  let name = raw.replace(/\s*\b(?:Pty\.?\s*Ltd\.?|Ltd\.?|Inc\.?|Pty)\b\s*/gi, '').trim();
  if (!name) return raw.trim();
  // Title case: first letter uppercase, rest lowercase for each word
  name = name
    .toLowerCase()
    .replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
  return name;
}

/**
 * Match filename to aggregator pattern.
 * Returns aggregator code or null if no match.
 */
function matchAggregator(filename: string): string | null {
  // ENTO codes
  const entoMatch = filename.match(/(?:^|[^A-Za-z0-9])(EN[A-Z]{1,2}\d{3})(?:[^A-Za-z0-9]|$)/i);
  if (entoMatch) return entoMatch[1].toUpperCase();

  // V-codes
  const vcodeMatch = filename.match(/(?:^|[^A-Za-z0-9])(V\d+PL)(?:[^A-Za-z0-9]|$)/i);
  if (vcodeMatch) return vcodeMatch[1].toUpperCase();

  // ENTORS
  if (/(?:^|[^A-Za-z0-9])ENTORS(?:[^A-Za-z0-9]|$)/i.test(filename)) return 'ENTORS';

  // ENTORUM
  if (/(?:^|[^A-Za-z0-9])ENTORUM(?:[^A-Za-z0-9]|$)/i.test(filename)) return 'ENTORUM';

  // AFG patterns (RCTI or COMM_STMT)
  if (/RCTI[-_]/i.test(filename) || /COMM_STMT[-_]/i.test(filename)) {
    if (/ENTORUM/i.test(filename)) return 'ENTORUM';
    if (/CRANBOURNE/i.test(filename)) return 'CRANBOURNE';
    return 'AFG';
  }

  // WLTH
  if (/wlth/i.test(filename)) return 'WLTH';

  // ORDE
  if (/orde/i.test(filename)) return 'ORDE';

  return null;
}

/**
 * Extract run number from filename or email date.
 */
function extractRunInfo(
  filename: string,
  emailDate: Date
): { runNumber: number; periodMonth: string } | null {
  // Try filename pattern first: "RUN 1", "RUN 2", "RUN 3"
  const runMatch = filename.match(/RUN\s*(\d)/i);
  const monthMatch = filename.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\s*(\d{2,4})/i);

  if (runMatch && monthMatch) {
    const runNumber = parseInt(runMatch[1], 10);
    const monthName = monthMatch[1].toLowerCase();
    let year = parseInt(monthMatch[2], 10);
    if (year < 100) year += 2000;

    const monthMap: Record<string, number> = {
      jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
      jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
    };
    const month = monthMap[monthName] ?? 0;
    const periodMonth = `${year}-${String(month + 1).padStart(2, '0')}`;

    return { runNumber, periodMonth };
  }

  // Fallback: derive from email date
  const day = emailDate.getDate();
  let runNumber = 1;
  if (day >= 11 && day <= 20) runNumber = 2;
  else if (day >= 21) runNumber = 3;

  const periodMonth = `${emailDate.getFullYear()}-${String(emailDate.getMonth() + 1).padStart(2, '0')}`;

  return { runNumber, periodMonth };
}

/**
 * Extract payment number and date info from RCTI filename.
 * Pattern: RCTI-{Company}-{YYYYMMDD}-{PaymentNumber}.pdf
 * Example: "RCTI-ENTOURAGE CRANBOURNE PTY LTD-20260306-1113194.pdf"
 *   → { paymentNumber: "1113194", date: "20260306", runNumber: 1, periodMonth: "2026-03" }
 */
function extractRctiPaymentInfo(filename: string): {
  paymentNumber: string;
  date: string;
  runNumber: number;
  periodMonth: string;
} | null {
  const match = filename.match(/RCTI[-_\s].+?[-_\s](\d{8})[-_\s](\d+)/i);
  if (!match) return null;
  const dateStr = match[1]; // "20260306"
  const paymentNumber = match[2]; // "1113194"
  const year = parseInt(dateStr.substring(0, 4));
  const month = parseInt(dateStr.substring(4, 6));
  const day = parseInt(dateStr.substring(6, 8));
  const runNumber = day <= 10 ? 1 : day <= 20 ? 2 : 3;
  const periodMonth = `${year}-${String(month).padStart(2, '0')}`;
  return { paymentNumber, date: dateStr, runNumber, periodMonth };
}

/**
 * Extract payment number from COMM_STMT or similar CSV filename.
 * Matches trailing numeric ID before extension: ...-MASTER-1113194.csv, ...-1113194.csv
 * Tolerates browser download suffixes like " (1)".
 */
function extractCsvPaymentNumber(filename: string): string | null {
  const match = filename.match(/[-_](\d{5,})(?:\s*\(\d+\))?\.\w+$/);
  return match ? match[1] : null;
}

/**
 * Post-scan: pair RCTI PDFs with COMM_STMT CSVs by payment number.
 * Propagates run/month metadata from the RCTI to its paired CSV,
 * and ensures both files share the same entityCode for grouping.
 */
function pairRctiWithCsvFiles(files: ScoutedFile[]): void {
  // Build map: paymentNumber → RCTI file + parsed info
  const rctiMap = new Map<string, { file: ScoutedFile; info: ReturnType<typeof extractRctiPaymentInfo> & {} }>();

  for (const file of files) {
    if (file.fileType === 'rcti') {
      const info = extractRctiPaymentInfo(file.filename);
      if (info) {
        rctiMap.set(info.paymentNumber, { file, info });
        // Apply run/month to the RCTI itself (overrides email-date fallback)
        file.runNumber = info.runNumber;
        file.periodMonth = info.periodMonth;
      }
    }
  }

  // Match CSVs to their RCTI by payment number
  for (const file of files) {
    if (file.fileType === 'data') {
      const paymentNum = extractCsvPaymentNumber(file.filename);
      if (paymentNum && rctiMap.has(paymentNum)) {
        const { file: rcti, info } = rctiMap.get(paymentNum)!;
        // Apply RCTI's run/month to the CSV (CSV has no date in filename)
        file.runNumber = info.runNumber;
        file.periodMonth = info.periodMonth;
        // Ensure both files share the same entityCode for loan book grouping
        if (file.entityCode && !rcti.entityCode) {
          rcti.entityCode = file.entityCode;
        } else if (rcti.entityCode && !file.entityCode) {
          file.entityCode = rcti.entityCode;
        }
        // Also sync aggregatorCode for consistency
        if (file.aggregatorCode && !rcti.aggregatorCode) {
          rcti.aggregatorCode = file.aggregatorCode;
        } else if (rcti.aggregatorCode && !file.aggregatorCode) {
          file.aggregatorCode = rcti.aggregatorCode;
        }
        // Sync entityName so paired files display the same friendly name
        if (file.entityName && !rcti.entityName) {
          rcti.entityName = file.entityName;
        } else if (rcti.entityName && !file.entityName) {
          file.entityName = rcti.entityName;
        }
        console.log(
          `[GmailScout] Paired: "${file.filename}" ↔ "${rcti.filename}" (payment #${paymentNum}, RUN ${info.runNumber}, ${info.periodMonth})`
        );
      }
    }
  }
}

/**
 * Scan Gmail for commission files.
 * This is the main function that orchestrates the entire scan process.
 */
export async function scanGmailForCommissions(
  accessToken: string,
  options: {
    senderPatterns?: string[];
    afterDate?: Date;
    beforeDate?: Date;
    maxMessages?: number;
    onProgress?: ProgressCallback;
    /** When true, skip aggregator matching — pull all valid attachments from matched senders.
     *  Used for new subscribers whose filename patterns are unknown. */
    stagingMode?: boolean;
    /** When true, the user provided custom sender emails — skip filename pattern filtering
     *  in the Gmail query and accept unmatched files from those senders. */
    hasCustomSenders?: boolean;
  } = {}
): Promise<ScoutedFile[]> {
  const {
    senderPatterns = KNOWN_COMMISSION_SENDERS,
    afterDate,
    beforeDate,
    maxMessages = 2000,
    onProgress,
    stagingMode = false,
    hasCustomSenders = false,
  } = options;

  const progress: ScanProgress = {
    phase: 'searching',
    messagesFound: 0,
    messagesScanned: 0,
    filesFound: 0,
    filesMatched: 0,
  };

  const reportProgress = () => onProgress?.(progress);

  try {
    // Initialize Gmail client
    await initGmailClient(accessToken);

    // Search for messages
    progress.phase = 'searching';
    reportProgress();

    const query = buildSearchQuery(senderPatterns, afterDate, stagingMode, hasCustomSenders, beforeDate);
    console.log('[GmailScout] Search query:', query);

    const messageIds = await searchMessages(query, maxMessages);
    progress.messagesFound = messageIds.length;
    progress.phase = 'listing';
    reportProgress();

    console.log(`[GmailScout] Found ${messageIds.length} messages`);

    // Analyze each message
    progress.phase = 'analyzing';
    const scoutedFiles: ScoutedFile[] = [];

    for (const messageId of messageIds) {
      try {
        const message = await getMessageDetails(messageId);
        progress.messagesScanned++;
        progress.currentMessage = getHeader(message.payload.headers, 'Subject') || messageId;
        reportProgress();

        // Extract email metadata
        const emailDate = new Date(parseInt(message.internalDate, 10));
        const subject = getHeader(message.payload.headers, 'Subject') || '';
        const from = getHeader(message.payload.headers, 'From') || '';
        const senderEmail = from.match(/<([^>]+)>/)?.[1] || from;

        // Extract attachments
        const attachments = extractAttachments(message.payload);
        progress.filesFound += attachments.length;
        reportProgress();

        for (const attachment of attachments) {
          // Filter: valid extension
          if (!hasValidExtension(attachment.filename)) continue;

          // Filter: not individual broker file
          if (isIndividualBrokerFile(attachment.filename)) continue;

          // Match aggregator (skipped in staging mode or when custom senders provided)
          const aggregatorCode = matchAggregator(attachment.filename);
          if (!stagingMode && !hasCustomSenders && !aggregatorCode) continue;

          // Entity code: use aggregatorCode when available, otherwise extract prefix from filename.
          // This ensures W4FI104, BRIS302 etc. get properly grouped even when matchAggregator() doesn't recognize them.
          const entityCode = aggregatorCode || extractEntityCodeFromFilename(attachment.filename);

          // Try to extract a human-friendly name from the filename
          const entityName = extractEntityNameFromFilename(attachment.filename);

          // Extract run info
          const runInfo = extractRunInfo(attachment.filename, emailDate);

          scoutedFiles.push({
            filename: attachment.filename,
            mimeType: attachment.mimeType,
            size: attachment.size,
            attachmentId: attachment.attachmentId,
            messageId,
            emailDate,
            emailSubject: subject,
            senderEmail,
            aggregatorCode,
            entityCode,
            entityName: entityName || undefined,
            runNumber: runInfo?.runNumber || null,
            periodMonth: runInfo?.periodMonth || null,
            fileType: isRctiPdf(attachment.filename) ? 'rcti' : 'data',
          });

          progress.filesMatched++;
          reportProgress();
        }
      } catch (error) {
        console.error(`[GmailScout] Error processing message ${messageId}:`, error);
        // Continue with next message
      }
    }

    // Post-scan: pair RCTI PDFs with COMM_STMT CSVs by payment number.
    // This propagates run/month from the RCTI (which has a date in its filename)
    // to the CSV (which only has a payment number), and ensures both files share
    // the same entityCode so they end up in the same loan book group.
    pairRctiWithCsvFiles(scoutedFiles);

    progress.phase = 'complete';
    reportProgress();

    console.log(`[GmailScout] Scan complete: ${scoutedFiles.length} files matched`);
    return scoutedFiles;
  } catch (error) {
    progress.phase = 'error';
    progress.error = (error as Error).message;
    reportProgress();
    throw error;
  }
}

/**
 * Download attachment content from Gmail.
 * Returns ArrayBuffer for hashing and uploading.
 */
export async function downloadAttachment(
  messageId: string,
  attachmentId: string
): Promise<ArrayBuffer> {
  const response = await window.gapi.client.gmail.users.messages.attachments.get({
    userId: 'me',
    messageId,
    id: attachmentId,
  });

  // Gmail returns base64url encoded data
  const base64Data = response.result.data;
  const binaryString = atob(base64Data.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Calculate SHA256 hash of file content (for deduplication).
 */
export async function calculateFileHash(content: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', content);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Declare gapi types
declare global {
  interface Window {
    gapi: {
      load: (api: string, callback: () => void) => void;
      client: {
        init: (config: { discoveryDocs: string[] }) => Promise<void>;
        setToken: (token: { access_token: string }) => void;
        gmail: {
          users: {
            messages: {
              list: (params: {
                userId: string;
                q: string;
                maxResults: number;
                pageToken?: string;
              }) => Promise<{
                result: {
                  messages?: Array<{ id: string }>;
                  nextPageToken?: string;
                };
              }>;
              get: (params: {
                userId: string;
                id: string;
                format: string;
              }) => Promise<{ result: GmailMessage }>;
              attachments: {
                get: (params: {
                  userId: string;
                  messageId: string;
                  id: string;
                }) => Promise<{ result: { data: string } }>;
              };
            };
          };
        };
      };
    };
  }
}
